import { DocComment } from "../documents/commentsAPI"

/**
 * Comment anchoring for the risk analysis views.
 *
 * The house convention for commenting on a structured record inside a section is
 * `sectionGuid` = the document section, `contextGuid` = the record's guid,
 * `contextType` = what kind of record it is — the same pair `SectionComments`
 * uses for tests, DQ mappings, questions and analyzed objects.
 *
 * The RA views historically did something else: they put the entity guid into
 * the `sectionGuid` slot and left `contextType`/`contextGuid` empty. That is why
 * a RISK_ANALYSIS section has always shown a comment count of zero in the
 * document tree — the counters there match on `sectionGuid === sectionId`.
 *
 * Everything written from here uses the convention; `matchesAnchor` still reads
 * the old shape so comments made before the change stay visible.
 */

export type RaAnchorKind =
    | 'function'
    | 'defect'
    | 'consequence'
    | 'reason'
    | 'control-method'
    | 'recommended-action'

/** The `contextType` written for each kind. Never empty — that is the point. */
export const raContextType: Record<RaAnchorKind, string> = {
    'function': 'ra_function',
    'defect': 'ra_defect',
    'consequence': 'ra_consequence',
    'reason': 'ra_reason',
    'control-method': 'ra_control_method',
    'recommended-action': 'ra_recommended_action',
}

/**
 * The four levels that existed before this change, and so the only ones that can
 * have comments in the legacy shape. The two field anchors are new — reading them
 * through the legacy branch would make every reason-level comment show up three
 * times, once per anchor sharing the reason's guid.
 */
const entityKinds: ReadonlySet<RaAnchorKind> = new Set<RaAnchorKind>([
    'function', 'defect', 'consequence', 'reason',
])

export interface RaAnchor {
    kind: RaAnchorKind
    /** Always set. */
    contextType: string
    /** The entity guid. For the two field anchors, the owning reason's guid. */
    contextGuid: string
    /** Gates the legacy read branch. */
    isEntityAnchor: boolean
    /** Plain-text entity name, for the target picker. */
    label: string
}

export const anchorOf = (kind: RaAnchorKind, guid: string, label: string): RaAnchor => ({
    kind,
    contextType: raContextType[kind],
    contextGuid: guid,
    isEntityAnchor: entityKinds.has(kind),
    label,
})

/** Older rows can come back with `contextType: null` rather than `''`. */
const ctxType = (c: DocComment): string => c.contextType || ''

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

/**
 * Whether a comment carries a real context anchor.
 *
 * Keyed on `contextType`, deliberately not on `contextGuid`: the column is
 * `uniqueidentifier NOT NULL` with an all-zeros default, so `contextGuid` is
 * never null in a response — and until the API was fixed, `AddComment` filled a
 * missing one with a fresh random Guid. Testing the guid would therefore call
 * every legacy row "anchored" to something that does not exist.
 */
const hasContext = (c: DocComment): boolean => {
    const t = ctxType(c)
    return t !== '' && !!c.contextGuid && c.contextGuid !== EMPTY_GUID
}

export const matchesAnchor = (c: DocComment, a: RaAnchor): boolean =>
    hasContext(c)
        ? c.contextGuid === a.contextGuid && ctxType(c) === a.contextType
        // Legacy: the entity guid sat in the sectionGuid slot, with no context.
        : a.isEntityAnchor && c.sectionGuid === a.contextGuid

/**
 * Top-level comments grouped by anchor, so a cell never runs a `.filter()`.
 *
 * An inline filter in a component would be cheap on its own; the problem is that
 * it returns a fresh array on every call, which defeats every memo downstream —
 * exactly the mistake that made `TestSection` re-render all 50 function rows on
 * every store action. Build this once, in a `useMemo`, and read it by key.
 */
export interface RaCommentIndex {
    /** Canonical rows, keyed `${contextGuid}|${contextType}`. */
    byContext: Map<string, DocComment[]>
    /** Legacy rows (no contextGuid), keyed by the guid found in `sectionGuid`. */
    byLegacySection: Map<string, DocComment[]>
    /**
     * Whether any row is anchored in this risk analysis.
     *
     * Only canonical `ra_*` rows count. The index is built from the whole
     * document's comments — legacy rows are indistinguishable from any other
     * section's section-level comment without the RA's entity guids, and walking
     * the function tree to get them would rebuild this on every save. So a
     * read-only reviewer whose analysis holds *only* pre-migration comments does
     * not get the table's comments column; the diagram views, where those
     * comments have always lived, still show them.
     */
    hasRaComments: boolean
}

const push = (m: Map<string, DocComment[]>, key: string, c: DocComment) => {
    const bucket = m.get(key)
    if (bucket) {
        bucket.push(c)
    } else {
        m.set(key, [c])
    }
}

const byCreateDate = (a: DocComment, b: DocComment) => a.createDate < b.createDate ? -1 : a.createDate > b.createDate ? 1 : 0

const raContextTypes: ReadonlySet<string> = new Set(Object.values(raContextType))

export const buildRaCommentIndex = (comments: DocComment[]): RaCommentIndex => {
    const byContext = new Map<string, DocComment[]>()
    const byLegacySection = new Map<string, DocComment[]>()
    let hasRaComments = false

    for (const c of comments) {
        // Replies hang off their parent's `replies` array; indexing them too would
        // render each one a second time as a thread of its own.
        if (c.parentCommentId !== null && c.parentCommentId !== undefined) continue

        if (hasContext(c)) {
            push(byContext, `${c.contextGuid}|${ctxType(c)}`, c)
            if (raContextTypes.has(ctxType(c))) hasRaComments = true
        } else {
            push(byLegacySection, c.sectionGuid, c)
        }
    }

    byContext.forEach(v => v.sort(byCreateDate))
    byLegacySection.forEach(v => v.sort(byCreateDate))

    return { byContext, byLegacySection, hasRaComments }
}

const EMPTY: DocComment[] = []

export const commentsFor = (index: RaCommentIndex, a: RaAnchor): DocComment[] => {
    const canonical = index.byContext.get(`${a.contextGuid}|${a.contextType}`)
    const legacy = a.isEntityAnchor ? index.byLegacySection.get(a.contextGuid) : undefined

    if (!canonical) return legacy ?? EMPTY
    if (!legacy) return canonical
    return [...canonical, ...legacy].sort(byCreateDate)
}

export const hasAnyRaComment = (index: RaCommentIndex): boolean => index.hasRaComments

/** The entities a single reason row sits under, innermost last. */
export interface RaRowPath {
    fn: { guid: string, name: string }
    defect: { guid: string, name: string }
    consequence: { guid: string, name: string }
    reason: { guid: string, name: string }
}

export interface RaRowFlags {
    isFirstDefect: boolean
    isFirstConsequence: boolean
    isFirstReason: boolean
}

export interface RaRowAnchors {
    /**
     * What renders on this row. A defect spans several reason rows, so its
     * comments render on the first of them — that way every comment appears
     * exactly once, next to the thing it is about.
     */
    owned: RaAnchor[]
}

/**
 * `strip` is passed in rather than imported so this module stays free of DOM
 * dependencies and can be unit-tested under plain jest.
 */
export const rowAnchors = (path: RaRowPath, flags: RaRowFlags, strip: (html: string) => string): RaRowAnchors => {
    const { fn, defect, consequence, reason } = path
    const { isFirstDefect, isFirstConsequence, isFirstReason } = flags

    const fnAnchor = anchorOf('function', fn.guid, strip(fn.name))
    const defectAnchor = anchorOf('defect', defect.guid, strip(defect.name))
    const consequenceAnchor = anchorOf('consequence', consequence.guid, strip(consequence.name))
    const reasonAnchor = anchorOf('reason', reason.guid, strip(reason.name))
    const ccmAnchor = anchorOf('control-method', reason.guid, strip(reason.name))
    const actionAnchor = anchorOf('recommended-action', reason.guid, strip(reason.name))

    const owned: RaAnchor[] = []
    if (isFirstDefect && isFirstConsequence && isFirstReason) owned.push(fnAnchor)
    if (isFirstConsequence && isFirstReason) owned.push(defectAnchor)
    if (isFirstReason) owned.push(consequenceAnchor)
    owned.push(reasonAnchor, ccmAnchor, actionAnchor)

    return { owned }
}
